import type { GetPreviousDataReturn } from '@/dashboards/shared/optimisation/previousData';
import { structureBudgetData, structureMediaBudget, withUncategorisedChannels } from '@/dashboards/utils/data';
import type { StructureMediaBudgetPeriod, StructureMediaBudgetReturn } from '@/dashboards/utils/data/structureMediaBudget';
import { useCallback, useMemo } from 'react';
import type { OptimizedPlan } from '@/dashboards/shared/optimisation/optimisationSession';
import type { ContributionLastYear } from './useCampaignHistoricalData';

const EMPTY_PREVIOUS = {
    roiCurves: { curves: {}, xAxis: [], variablesAndCategories: { byCategory: {}, byVariable: {} } },
    contributionsByCategory: {},
    totals: {},
    rows: {},
    totalContribution: 0,
    mediaContribution: 0,
} as unknown as GetPreviousDataReturn;

export type CampaignDashboardData = ReturnType<typeof useCampaignDashboardData>;

export const useCampaignDashboardData = ({
    result,
    previous,
    variableNameMap,
    contributionLastYear,
}: {
    result: OptimizedPlan | null;
    previous: GetPreviousDataReturn | undefined;
    variableNameMap: Record<string, string>;
    contributionLastYear: ContributionLastYear;
}) => {
    const optimizedData = result ?? undefined;
    const prev = useMemo(() => withUncategorisedChannels(previous ?? EMPTY_PREVIOUS, optimizedData), [previous, optimizedData]);
    const { variablesAndCategories } = prev.roiCurves;

    const structured = useMemo(
        () => structureBudgetData({ optimizedData, variablesAndCategories, totals: prev.totals, variableNameMap }),
        [optimizedData, variablesAndCategories, prev.totals, variableNameMap],
    );

    const getMediaBudgetTableData = useCallback(
        (period: StructureMediaBudgetPeriod): StructureMediaBudgetReturn[] =>
            structureMediaBudget(period, { optimizedData, variablesAndCategories, variableNameMap }),
        [optimizedData, variablesAndCategories, variableNameMap],
    );

    const nonMediaKpi = (prev.totalContribution || 0) - (prev.mediaContribution || 0);
    const contributionTotalWithNewMedia = optimizedData?.totalKpi ?? nonMediaKpi + structured.contributionTotal;

    return useMemo(
        () => ({
            optimized: optimizedData,
            previous: prev,
            suggestedBudgetData: structured.suggestedBudgetData,
            budgetByCategoryData: structured.budgetByCategoryData,
            contributionTotal: structured.contributionTotal,
            simulatedInvestmentTotal: structured.simulatedInvestmentTotal,
            expectedContributionByCategoryData: structured.expectedContributionByCategoryData,
            contributionTotalWithNewMedia,
            contributionLastYear,
            getMediaBudgetTableData,
        }),
        [optimizedData, prev, structured, contributionTotalWithNewMedia, contributionLastYear, getMediaBudgetTableData],
    );
};
